import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import {
  VedicAuthSession,
  getAuthSessionAsync,
  fetchBetterAuthSession,
  saveAuthSession,
  clearAuthSession,
  SESSION_COOKIE_NAME,
  getSharedCookie
} from "../lib/authSession";

export interface BetterAuthUser {
  id: string;
  email: string;
  name?: string;
  tier?: "PRO" | "LIFETIME_FOUNDER" | "GENERAL";
  isPro?: boolean;
  role?: string;
  image?: string;
}

export interface BetterAuthContextValue {
  session: VedicAuthSession | null;
  user: BetterAuthUser | null;
  tier: "PRO" | "LIFETIME_FOUNDER" | "GENERAL";
  isAuthenticated: boolean;
  isLoading: boolean;
  refreshSession: () => Promise<VedicAuthSession | null>;
  login: () => void;
  logout: () => void;
  updateTier: (newTier: "PRO" | "LIFETIME_FOUNDER" | "GENERAL") => void;
}

const BetterAuthContext = createContext<BetterAuthContextValue | null>(null);

export function BetterAuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<VedicAuthSession | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Sync user object and entitlement directly from Better Auth / session
  const user = useMemo<BetterAuthUser | null>(() => {
    if (!session) return null;
    return {
      id: session.userId,
      email: session.email,
      tier: session.tier,
      isPro: session.tier === "PRO" || session.tier === "LIFETIME_FOUNDER"
    };
  }, [session]);

  const tier = session?.tier || "GENERAL";
  const isAuthenticated = !!session;

  const refreshSession = useCallback(async (): Promise<VedicAuthSession | null> => {
    try {
      const activeSession = await getAuthSessionAsync();
      setSession(activeSession);
      return activeSession;
    } catch (err) {
      console.warn("[BetterAuthProvider]: Error loading session:", err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();

    // Event-driven reactive sync across browser tabs and monolith custom events
    const handleAuthChange = () => {
      refreshSession();
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "vedic_auth_token" || e.key === SESSION_COOKIE_NAME) {
        refreshSession();
      }
    };

    window.addEventListener("auth-changed", handleAuthChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("auth-changed", handleAuthChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [refreshSession]);

  const login = useCallback(() => {
    if (typeof window !== "undefined") {
      const redirectUrl = encodeURIComponent(window.location.href);
      const loginUrl = window.location.hostname.includes("bhutadamarasena.com")
        ? `/login?redirect=${redirectUrl}`
        : `https://bhutadamarasena.com/login?redirect=${redirectUrl}`;
      window.location.href = loginUrl;
    }
  }, []);

  const logout = useCallback(() => {
    clearAuthSession();
    setSession(null);
    window.dispatchEvent(new CustomEvent("auth-changed"));
  }, []);

  const updateTier = useCallback((newTier: "PRO" | "LIFETIME_FOUNDER" | "GENERAL") => {
    if (session) {
      const updated: VedicAuthSession = { ...session, tier: newTier };
      saveAuthSession(updated);
      setSession(updated);
      window.dispatchEvent(new CustomEvent("auth-changed"));
    }
  }, [session]);

  const contextValue = useMemo<BetterAuthContextValue>(
    () => ({
      session,
      user,
      tier,
      isAuthenticated,
      isLoading,
      refreshSession,
      login,
      logout,
      updateTier
    }),
    [session, user, tier, isAuthenticated, isLoading, refreshSession, login, logout, updateTier]
  );

  return (
    <BetterAuthContext.Provider value={contextValue}>
      {children}
    </BetterAuthContext.Provider>
  );
}

export function useBetterAuth(): BetterAuthContextValue {
  const ctx = useContext(BetterAuthContext);
  if (!ctx) {
    throw new Error("useBetterAuth must be used within a BetterAuthProvider");
  }
  return ctx;
}
